<template>
  <van-popup v-model="show" position="center" round
    :style="{ width: '90%', maxWidth: '420px' }">
    <div class="contact-detail-popup">
      <!-- 用户信息头部 -->
      <div class="contact-header">
        <div class="contact-avatar" @click="previewAvatar">
          <img v-if="userAvatar" :src="$common.getFullImageUrl(userAvatar, $config, 'm')" class="avatar-img" />
          <div v-else class="avatar-placeholder">{{ (userName || $t('anonymous'))[0] }}</div>
        </div>
        <div class="contact-name">{{ userName || $t('default_username') }}</div>
        <div v-if="userBio" class="contact-bio">{{ userBio }}</div>
        <div v-else-if="isBioLoading" class="contact-bio-loading">
          <van-loading size="16" color="#999" />
        </div>
      </div>

      <!-- 消息免打扰（仅私聊 contact 场景展示，由后端 hasContact 判断） -->
      <div v-if="dndVisible" style="display: flex; align-items: center; justify-content: space-between; padding: 12px 4px; margin-top: 12px; border-bottom: 1px solid #F0F0F0;">
        <span style="font-size: 14px; color: #4A4A4A;">{{ $t('contacts_dnd') }}</span>
        <van-switch v-model="dnd" size="20px" active-color="#E8543A" :disabled="dndProcessing" @change="toggleDnd" />
      </div>

      <!-- Block 状态提示 -->
      <template v-if="showBlockActions">
        <div v-if="isBlocked" class="block-status-banner">
          <van-icon name="warning-o" color="#E05252" size="16" />
          <span>{{ $t('block_status_banner') }}</span>
        </div>

        <!-- Block/Unblock 按钮 -->
        <div class="block-action-section">
          <div v-if="isLoading" class="loading-block-status">
            <van-loading size="20" />
          </div>
          <template v-else>
            <van-button 
              v-if="isBlocked" 
              block 
              color="#34A853"
              size="large" 
              @click="unblockUser"
              :loading="isProcessing"
            >
              <template #default>
                <span style="display: flex; align-items: center; justify-content: center;">
                  <van-icon name="passed" style="margin-right: 4px;" />
                  <span>{{ $t('unblock_user') }}</span>
                </span>
              </template>
            </van-button>
            <van-button 
              v-else 
              block 
              plain 
              type="danger" 
              size="large" 
              @click="blockUser"
              :loading="isProcessing"
            >
              <template #default>
                <span style="display: flex; align-items: center; justify-content: center;">
                  <van-icon name="warning-o" style="margin-right: 4px;" />
                  <span>{{ $t('block_user') }}</span>
                </span>
              </template>
            </van-button>
          </template>
        </div>
      </template>

      <van-button block type="default" size="large" class="btn" @click="close" :style="showBlockActions ? 'margin-top: 12px;' : 'margin-top: 24px;'">
        {{ $t('close') }}
      </van-button>
    </div>
  </van-popup>
</template>

<script>
import Vue from 'vue'
import { Popup, Cell, CellGroup, Icon, Button, Toast, Loading, Dialog, ImagePreview, Switch } from 'vant'
import axios from 'axios'

Vue.use(Popup)
Vue.use(Cell)
Vue.use(CellGroup)
Vue.use(Icon)
Vue.use(Button)
Vue.use(Toast)
Vue.use(Loading)
Vue.use(Dialog)
Vue.use(ImagePreview)
Vue.use(Switch)

export default {
  name: 'ContactMethods',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    contactInfo: {
      type: Object,
      default: () => ({})
    },
    targetUserId: {
      type: String,
      default: ''
    },
    userName: {
      type: String,
      default: ''
    },
    userAvatar: {
      type: String,
      default: ''
    },
    userBio: {
      type: String,
      default: ''
    },
    isBioLoading: {
      type: Boolean,
      default: false
    },
    showBlockActions: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      show: this.visible,
      isBlocked: false,
      isLoading: false,
      isProcessing: false,
      dnd: false, // 当前查看者对该私聊的免打扰开关
      dndVisible: false, // 仅当双方存在私聊 contact 时展示
      dndProcessing: false
    }
  },
  watch: {
    visible(newVal) {
      this.show = newVal
      if (newVal && this.targetUserId) {
        this.checkBlockStatus()
        this.loadDnd()
      }
    },
    show(newVal) {
      this.$emit('update:visible', newVal)
    }
  },
  methods: {
    close() {
      this.show = false
    },
    previewAvatar () {
      if (!this.userAvatar) return
      ImagePreview({
        images: [this.$common.getFullImageUrl(this.userAvatar, this.$config, 'l')],
        closeable: true
      })
    },
    // 查询当前用户对该联系人的私聊免打扰状态
    async loadDnd() {
      if (!this.targetUserId) return
      try {
        const response = await axios.post('/api/chat/get_contact_dnd', {
          targetUserId: this.targetUserId
        })
        if (response.data && response.data.code === 0 && response.data.data) {
          // hasContact=0（如匹配会话场景还没有私聊）时不展示免打扰入口
          this.dndVisible = response.data.data.hasContact == 1
          this.dnd = response.data.data.dnd == 1
        } else {
          this.dndVisible = false
        }
      } catch (error) {
        console.error('Failed to load dnd status:', error)
        this.dndVisible = false
      }
    },
    // 切换私聊免打扰
    async toggleDnd() {
      if (!this.targetUserId || this.dndProcessing) return
      this.dndProcessing = true
      try {
        await axios.post('/api/chat/update_contact_dnd', {
          targetUserId: this.targetUserId,
          dnd: this.dnd ? 1 : 0
        })
        // 同步免打扰映射（WS handler 跳过判断用）
        this.$store.commit('setContactsDnd', { userId: this.targetUserId, dnd: this.dnd ? 1 : 0 })
        Toast.success(this.dnd ? this.$t('dnd_enabled') : this.$t('dnd_disabled'))
      } catch (error) {
        console.error('Failed to toggle dnd:', error)
        this.dnd = !this.dnd
        Toast.fail(this.$t('toast_mark_all_read_failed'))
      } finally {
        this.dndProcessing = false
      }
    },
    async checkBlockStatus() {
      if (!this.targetUserId) return
      
      this.isLoading = true
      try {
        const response = await axios.post('/api/user/is_blocked', {
          targetUserId: this.targetUserId
        })
        
        if (response.data && response.data.code === 0 && response.data.data) {
          // isBlockingTarget: 当前用户是否屏蔽了目标用户
          this.isBlocked = response.data.data.isBlockingTarget === true
        }
      } catch (error) {
        console.error('Failed to check block status:', error)
      } finally {
        this.isLoading = false
      }
    },
    async blockUser() {
      if (!this.targetUserId) {
        Toast(this.$t('toast_block_missing_id'))
        return
      }
      
      // 确认对话框
      try {
        await Dialog.confirm({
          title: this.$t('block_user'),
          message: this.$t('dialog_block_confirm', {name: this.userName || this.$t('default_username')}),
          confirmButtonText: this.$t('block'),
          confirmButtonColor: '#E05252',
          cancelButtonText: this.$t('cancel')
        })
      } catch (error) {
        // 用户取消
        return
      }
      
      this.isProcessing = true
      try {
        const response = await axios.post('/api/user/block_user', {
          blockedUserId: this.targetUserId
        })
        
        if (response.data && (response.data.code === 0 || response.data.success === true)) {
          this.isBlocked = true
          Toast.success(this.$t('toast_user_blocked', {name: this.userName || this.$t('default_username')}))
        } else {
          throw new Error(response.data.msg || this.$t('toast_block_failed'))
        }
      } catch (error) {
        console.error('Failed to block user:', error)
        Toast.fail(this.$t('toast_block_failed') + ': ' + (error.message || 'Please try again'))
      } finally {
        this.isProcessing = false
      }
    },
    async unblockUser() {
      if (!this.targetUserId) {
        Toast(this.$t('toast_unblock_missing_id'))
        return
      }
      
      this.isProcessing = true
      try {
        const response = await axios.post('/api/user/unblock_user', {
          blockedUserId: this.targetUserId
        })
        
        if (response.data && (response.data.code === 0 || response.data.success === true)) {
          this.isBlocked = false
          Toast.success(this.$t('toast_user_unblocked', {name: this.userName || this.$t('default_username')}))
        } else {
          throw new Error(response.data.msg || 'Failed to unblock user')
        }
      } catch (error) {
        console.error('Failed to unblock user:', error)
        Toast.fail(this.$t('toast_block_failed'))
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style scoped>
.contact-detail-popup {
  max-height: 70vh;
  overflow-y: auto;
  /* padding 放内层容器，不放 popup 本身（避免 width + padding 盒模型溢出） */
  padding: 20px 16px 16px;
}

/* 头部样式 */
.contact-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #E0DDD8;
  margin-bottom: 16px;
}

.contact-avatar {
  width: 64px;
  height: 64px;
  border-radius: 16%;
  overflow: hidden;
  margin-bottom: 12px;
  background-color: #F7F5F2;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #E05252;
  color: white;
  font-size: 24px;
  font-weight: 600;
}

.contact-name {
  font-size: 18px;
  font-weight: 600;
  color: #1A1A1A;
  text-align: center;
}

.contact-bio {
  font-size: 13px;
  color: #8A8A8A;
  text-align: center;
  margin-top: 4px;
  word-break: break-all;
  max-width: 260px;
}

.contact-bio-loading {
  display: flex;
  justify-content: center;
  margin-top: 4px;
  min-height: 20px;
}

/* Block 状态横幅 */
.block-status-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background-color: #FDEAEA;
  border: 1px solid #FDEAEA;
  border-radius: 8px;
  margin: 0 16px 16px;
  color: #E05252;
  font-size: 14px;
}

/* Block 操作区域 */
.block-action-section {
  margin: 16px;
}

.loading-block-status {
  display: flex;
  justify-content: center;
  padding: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/ContactMethods.vue